import React, { useEffect, useState } from "react";
import {
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  Button,
  Iconography,
  IconographySizes,
  Loader,
  LoaderSizes,
  Notification,
  NotificationVariants,
} from "@hid-galaxy-ui/galaxy-react";
import { useAppDispatch, useAppSelector } from "../../../../../../redux";
import {
  createIntegrationSagaAction,
  createTechnologySagaAction,
  deleteTechnologySagaAction,
  getTechnologiesSagaAction,
  updateIntegrationSagaAction,
  updateTechnologySagaAction,
} from "../../../../../../redux/saga/technology-settings/technologies/saga-actions";
import { mapIntegrationFormToApiPayload } from "../../../../../../redux/slices/technology-settings/technologies/integrationMapper";
import { technologySettingsTechnologiesSliceAction } from "../../../../../../redux/slices/technology-settings/technologies";
import type { IntegrationItem, TechnologyItem } from "../types";
import ViewDescriptionModal from "../components/ViewDescriptionModal";
import RichTextPreview from "../components/RichTextPreview";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import AddTechnologyModal, { TechnologyFormData } from "./AddTechnologyModal";
import AddIntegrationModal, { IntegrationFormData } from "./AddIntegrationModal";

const Technologies: React.FC<{ readOnly?: boolean }> = ({
  readOnly = false,
}) => {
  const dispatch = useAppDispatch();
  const technologies = useAppSelector(
    (state) => state.technology_settings_technologies.technologies
  );
  const isTechnologiesLoading = useAppSelector(
    (state) => state.technology_settings_technologies.status.technologiesLoading
  );
  const isCreateTechnologyLoading = useAppSelector(
    (state) => state.technology_settings_technologies.status.createTechnologyLoading
  );
  const isUpdateTechnologyLoading = useAppSelector(
    (state) => state.technology_settings_technologies.status.updateTechnologyLoading
  );
  const isTechnologySubmitting =
    isCreateTechnologyLoading || isUpdateTechnologyLoading;
  const isDeleteTechnologyLoading = useAppSelector(
    (state) => state.technology_settings_technologies.status.deleteTechnologyLoading
  );
  const isCreateIntegrationLoading = useAppSelector(
    (state) => state.technology_settings_technologies.status.createIntegrationLoading
  );
  const isUpdateIntegrationLoading = useAppSelector(
    (state) => state.technology_settings_technologies.status.updateIntegrationLoading
  );
  const isIntegrationSubmitting =
    isCreateIntegrationLoading || isUpdateIntegrationLoading;
  const [toasts, setToasts] = useState<any[]>([]);
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [isTechnologyModalOpen, setIsTechnologyModalOpen] = useState(false);
  const [technologyModalMode, setTechnologyModalMode] = useState<"add" | "edit">(
    "add"
  );
  const [editingTechnology, setEditingTechnology] = useState<TechnologyItem | null>(
    null
  );
  const [isIntegrationModalOpen, setIsIntegrationModalOpen] = useState(false);
  const [integrationModalMode, setIntegrationModalMode] = useState<"add" | "edit">(
    "add"
  );
  const [integrationTechnologyId, setIntegrationTechnologyId] = useState<
    string | null
  >(null);
  const [editingIntegration, setEditingIntegration] =
    useState<IntegrationItem | null>(null);
  const [descriptionView, setDescriptionView] = useState<{
    title: string;
    content: string;
  } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    type: "technology" | "integration";
    technologyId: string;
    integrationId?: string;
    name: string;
  } | null>(null);

  useEffect(() => {
    dispatch(getTechnologiesSagaAction());
  }, [dispatch]);

  const pushToast = (variant: "success" | "error", message: string) => {
    setToasts((prev) => [
      ...prev,
      {
        id: Date.now(),
        title: "",
        message,
        variant:
          variant === "success"
            ? NotificationVariants.Success
            : NotificationVariants.Error,
        icon: (
          <Iconography
            icon={variant === "success" ? "circleCheck" : "ban"}
            size={IconographySizes.Medium}
          />
        ),
      },
    ]);
  };

  const updateTechnologies = (
    updater: (current: TechnologyItem[]) => TechnologyItem[]
  ) => {
    dispatch(
      technologySettingsTechnologiesSliceAction.setTechnologies(
        updater(technologies)
      )
    );
  };

  const toggleRow = (technologyId: string) => {
    setExpandedRows((prev) =>
      prev.includes(technologyId)
        ? prev.filter((id) => id !== technologyId)
        : [...prev, technologyId]
    );
  };

  const closeTechnologyModal = () => {
    setIsTechnologyModalOpen(false);
    setEditingTechnology(null);
  };

  const closeIntegrationModal = () => {
    dispatch(technologySettingsTechnologiesSliceAction.setIntegrationDetail(null));
    setIsIntegrationModalOpen(false);
    setIntegrationTechnologyId(null);
    setEditingIntegration(null);
  };

  const handleAddTechnology = (data: TechnologyFormData) => {
    dispatch(
      createTechnologySagaAction({
        data: {
          technology_name: data.name.trim(),
          description: data.description,
          status: data.status ? 1 : 0,
        },
        onSuccess: (message) => {
          pushToast("success", message || "Technology added successfully.");
          closeTechnologyModal();
        },
        onError: (message) => {
          pushToast("error", message || "Unable to create technology.");
        },
      })
    );
  };

  const handleEditTechnology = (data: TechnologyFormData) => {
    if (!editingTechnology) return;
    dispatch(
      updateTechnologySagaAction({
        id: editingTechnology.id,
        data: {
          technology_name: data.name.trim(),
          description: data.description,
          status: data.status ? 1 : 0,
        },
        onSuccess: (message) => {
          pushToast("success", message || "Technology updated successfully.");
          closeTechnologyModal();
        },
        onError: (message) => {
          pushToast("error", message || "Unable to update technology.");
        },
      })
    );
  };

  const handleAddIntegration = (data: IntegrationFormData) => {
    if (!integrationTechnologyId) return;
    dispatch(
      createIntegrationSagaAction({
        technologyId: integrationTechnologyId,
        data: mapIntegrationFormToApiPayload(data),
        onSuccess: (message) => {
          pushToast("success", message || "Integration added successfully.");
          closeIntegrationModal();
        },
        onError: (message) => {
          pushToast("error", message || "Unable to create integration.");
        },
      })
    );
  };

  const handleEditIntegration = (data: IntegrationFormData) => {
    if (!editingIntegration) return;
    dispatch(
      updateIntegrationSagaAction({
        id: editingIntegration.id,
        data: mapIntegrationFormToApiPayload(data),
        onSuccess: (message) => {
          pushToast("success", message || "Integration updated successfully.");
          closeIntegrationModal();
        },
        onError: (message) => {
          pushToast("error", message || "Unable to update integration.");
        },
      })
    );
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;

    if (deleteTarget.type === "technology") {
      if (isDeleteTechnologyLoading) return;
      const target = deleteTarget;
      dispatch(
        deleteTechnologySagaAction({
          id: target.technologyId,
          onSuccess: (message) => {
            pushToast("success", message || "Technology deleted successfully.");
            setExpandedRows((prev) =>
              prev.filter((id) => id !== target.technologyId)
            );
            setDeleteTarget(null);
          },
          onError: (message) => {
            pushToast("error", message || "Unable to delete technology.");
          },
        })
      );
      return;
    }

    if (deleteTarget.integrationId) {
      updateTechnologies((prev) =>
        prev.map((technology) =>
          technology.id === deleteTarget.technologyId
            ? {
                ...technology,
                integrations: technology.integrations.filter(
                  (integration) => integration.id !== deleteTarget.integrationId
                ),
              }
            : technology
        )
      );
    }
    setDeleteTarget(null);
  };

  return (
    <div className="technology-management-content feature-management-content">
      <Notification toasts={toasts} setToasts={setToasts} />
      {isTechnologiesLoading ? (
        <div className="feature-loader-wrap">
          <Loader variant={LoaderSizes.large} />
        </div>
      ) : (
      <div className="features-section">
        <div className="fm-top-row tm-section-header">
          <div>
            <Typography variant="h6" className="section-title">
              Technologies
            </Typography>
            <Typography className="section-subtitle">
              Configure Technologies
            </Typography>
          </div>
          <div className="fm-actions">
            <Button
              variant="primary"
              label="Add a New Technology"
              icon={<Iconography icon="plus" />}
              disabled={readOnly}
              onClick={() => {
                if (readOnly) return;
                setTechnologyModalMode("add");
                setEditingTechnology(null);
                setIsTechnologyModalOpen(true);
              }}
            />
          </div>
        </div>

        <TableContainer component={Paper} className="features-table">
          <Table className="features-main-table tm-technologies-table">
            <colgroup>
              <col className="col-feature-name" />
              <col className="col-description" />
              <col className="col-status" />
              <col className="col-actions" />
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell className="feature-col-feature">Technology Name</TableCell>
                <TableCell className="feature-col-desc">Description</TableCell>
                <TableCell className="feature-col-status">Status</TableCell>
                <TableCell className="feature-actions-col">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {technologies.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="feature-empty-cell">
                    No technologies available.
                  </TableCell>
                </TableRow>
              )}
              {technologies.map((technology) => {
                const isExpanded = expandedRows.includes(technology.id);

                return (
                  <React.Fragment key={technology.id}>
                    <TableRow>
                      <TableCell className="feature-col-feature">
                        <div className="feature-name-cell">
                          <Button
                            variant="flat"
                            className="feature-expand-btn"
                            onClick={() => toggleRow(technology.id)}
                            icon={
                              <Iconography
                                icon={isExpanded ? "angleUp" : "angleDown"}
                                size={IconographySizes.Small}
                              />
                            }
                            aria-label={`${isExpanded ? "Collapse" : "Expand"} ${technology.name}`}
                          />
                          <span>{technology.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="feature-col-desc">
                        <button
                          type="button"
                          className="view-link"
                          onClick={() =>
                            setDescriptionView({
                              title: technology.name,
                              content: technology.description,
                            })
                          }
                        >
                          View
                        </button>
                      </TableCell>
                      <TableCell className="feature-col-status">
                        <Chip
                          label={technology.status}
                          className={`status-chip ${technology.status.toLowerCase()}`}
                        />
                      </TableCell>
                      <TableCell className="feature-actions-col">
                        <div className="feature-actions-cell">
                          <Button
                            variant="flat"
                            icon={<Iconography icon="pen" />}
                            disabled={readOnly}
                            onClick={() => {
                              if (readOnly) return;
                              setTechnologyModalMode("edit");
                              setEditingTechnology(technology);
                              setIsTechnologyModalOpen(true);
                            }}
                          />
                          <Button
                            variant="flat"
                            icon={<Iconography icon="trashCan" />}
                            disabled
                          />
                        </div>
                      </TableCell>
                    </TableRow>

                    {isExpanded && (
                      <TableRow className="expanded-details-row">
                        <TableCell colSpan={4} className="expanded-details-cell">
                          <div className="feature-details-card">
                            <div className="tm-details-card-header">
                              <Typography className="tm-details-card-header-title">
                                Integration
                              </Typography>
                              <Button
                                variant="flat"
                                className="tm-details-card-header-action"
                                label="+ Add New Integration"
                                disabled={readOnly}
                                onClick={() => {
                                  if (readOnly) return;
                                  setIntegrationModalMode("add");
                                  setIntegrationTechnologyId(technology.id);
                                  setEditingIntegration(null);
                                  setIsIntegrationModalOpen(true);
                                }}
                              />
                            </div>
                            <Table
                              size="small"
                              className="feature-details-table tm-integration-details-table"
                            >
                              <colgroup>
                                <col className="detail-col-1" />
                                <col className="detail-col-2" />
                                <col className="detail-col-3" />
                                <col className="detail-col-4" />
                              </colgroup>
                              <TableHead>
                                <TableRow>
                                  <TableCell className="detail-th-tech">
                                    Integration Name
                                  </TableCell>
                                  <TableCell className="detail-th-req">
                                    Description
                                  </TableCell>
                                  <TableCell className="detail-th-support">
                                    Status
                                  </TableCell>
                                  <TableCell className="detail-th-actions">
                                    Actions
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {technology.integrations.length === 0 && (
                                  <TableRow>
                                    <TableCell colSpan={4} className="feature-empty-cell">
                                      No integrations added yet.
                                    </TableCell>
                                  </TableRow>
                                )}
                                {technology.integrations.map((integration) => (
                                  <TableRow key={integration.id}>
                                    <TableCell className="detail-td-tech">
                                      {integration.name}
                                    </TableCell>
                                    <TableCell className="detail-td-req">
                                      <RichTextPreview content={integration.description} />
                                    </TableCell>
                                    <TableCell className="detail-td-support">
                                      <Chip
                                        label={integration.status}
                                        className={`detail-status-chip ${
                                          integration.status === "Active"
                                            ? "supported"
                                            : "not-supported"
                                        }`}
                                      />
                                    </TableCell>
                                    <TableCell className="detail-td-actions">
                                      <div className="feature-actions-cell">
                                        <Button
                                          variant="flat"
                                          icon={<Iconography icon="pen" />}
                                          disabled={readOnly}
                                          onClick={() => {
                                            if (readOnly) return;
                                            setIntegrationModalMode("edit");
                                            setIntegrationTechnologyId(technology.id);
                                            setEditingIntegration(integration);
                                            setIsIntegrationModalOpen(true);
                                          }}
                                        />
                                        <Button
                                          variant="flat"
                                          icon={<Iconography icon="trashCan" />}
                                          disabled
                                        />
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      )}

      <AddTechnologyModal
        open={isTechnologyModalOpen}
        mode={technologyModalMode}
        initialData={
          editingTechnology
            ? {
                name: editingTechnology.name,
                description: editingTechnology.description,
                status: editingTechnology.status === "Active",
              }
            : null
        }
        onClose={closeTechnologyModal}
        isSubmitting={isTechnologySubmitting}
        onSubmit={
          technologyModalMode === "add"
            ? handleAddTechnology
            : handleEditTechnology
        }
      />

      <AddIntegrationModal
        open={isIntegrationModalOpen}
        mode={integrationModalMode}
        integrationId={editingIntegration?.id}
        technologyName={
          technologies.find((technology) => technology.id === integrationTechnologyId)
            ?.name || "Technology"
        }
        onClose={closeIntegrationModal}
        isSubmitting={isIntegrationSubmitting}
        onSubmit={
          integrationModalMode === "add"
            ? handleAddIntegration
            : handleEditIntegration
        }
      />

      <ViewDescriptionModal
        open={Boolean(descriptionView)}
        eyebrow="Technology Management"
        title="View Description"
        subtitle={descriptionView?.title || ""}
        content={descriptionView?.content || ""}
        onClose={() => setDescriptionView(null)}
      />

      <DeleteConfirmModal
        open={Boolean(deleteTarget)}
        title={
          deleteTarget?.type === "technology"
            ? "Delete Technology?"
            : "Delete Integration?"
        }
        itemName={deleteTarget?.name || ""}
        message="Are you sure you want to delete"
        isSubmitting={
          deleteTarget?.type === "technology" ? isDeleteTechnologyLoading : false
        }
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};

export default Technologies;
